import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@signet/web-kit";
import { DocumentIcon, LinkIcon } from "@signet/ui";
import { classify, linksIn, readNotes, type NoteLink } from "../shared/links.ts";
import { BASE } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Notes with their links as chips (Jay, 2026-10-08). A Google file's chip
   carries the file's own name ("IG SUICIDE"), looked up through the shared
   Google account; until then, or when it cannot be seen, what it is
   ("Google Sheet"). Any other link shows its site. In the editor a link turns
   into its chip as soon as it is typed out or pasted.
   ────────────────────────────────────────────────────────────────────────── */

const GOOGLE = new Set(["sheet", "doc", "slides", "form", "folder", "drive"]);

/** Names of the Google files among `links`, as one request, remembered for the session. */
export function useLinkTitles(links: NoteLink[]): Record<string, string> {
  const urls = [...new Set(links.filter((l) => GOOGLE.has(l.kind)).map((l) => l.url))].sort();
  const titles = useQuery({
    queryKey: ["todo", "link-titles", urls],
    queryFn: () => api<{ titles: Record<string, string> }>(`${BASE}/link-titles?${urls.map((u) => `url=${encodeURIComponent(u)}`).join("&")}`),
    enabled: urls.length > 0,
    staleTime: Infinity,
    retry: false,
  });
  return titles.data?.titles ?? {};
}

export function LinkChip({ link, title }: { link: NoteLink; title?: string }) {
  const Icon = link.kind === "web" ? LinkIcon : DocumentIcon;
  return (
    <a className={`td-chip td-chip-${link.kind}`} href={link.url} target="_blank" rel="noreferrer noopener" title={title ? `${link.label}: ${title}` : link.url} onClick={(e) => e.stopPropagation()}>
      <Icon size={13} />
      <span className="td-chip-text">{title ?? link.label}</span>
    </a>
  );
}

/** Just the chips, for a line in the list. */
export function NoteChips({ notes, max = 3 }: { notes: string; max?: number }) {
  const links = linksIn(notes);
  const titles = useLinkTitles(links);
  if (!links.length) return null;
  return (
    <span className="td-row-chips">
      {links.slice(0, max).map((link, i) => (
        <LinkChip key={i} link={link} title={titles[link.url]} />
      ))}
      {links.length > max ? <span className="td-more">+{links.length - max}</span> : null}
    </span>
  );
}

// ── the editor ─────────────────────────────────────────────────────────────

const URL_AT_END = /(https?:\/\/[^\s<>"')\]]+?)([.,;:!?]*)$/i;

function chipElement(link: NoteLink, title?: string): HTMLElement {
  const chip = document.createElement("span");
  chip.className = `td-chip td-chip-${link.kind} td-chip-inline`;
  chip.contentEditable = "false";
  chip.dataset.url = link.url;
  chip.title = link.url;
  chip.textContent = title ?? link.label;
  return chip;
}

/** The editor's content as plain text, each chip as its address. */
function serialise(root: HTMLElement): string {
  let out = "";
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) out += node.textContent ?? "";
    else if (node instanceof HTMLElement && node.dataset.url) out += node.dataset.url;
    else if (node instanceof HTMLElement && node.tagName === "BR") out += "\n";
    else if (node instanceof HTMLElement) out += (out && !out.endsWith("\n") ? "\n" : "") + serialise(node);
  });
  return out;
}

function render(root: HTMLElement, notes: string, titles: Record<string, string>) {
  root.replaceChildren(
    ...readNotes(notes).map((part) => ("link" in part ? chipElement(part.link, titles[part.link.url]) : document.createTextNode(part.text))),
  );
}

/**
 * Turns links into chips. With `whole`, every link (after a paste);
 * otherwise only one the typing has just finished, which is one followed by
 * the space or line break just typed, so a link half typed stays text.
 */
function chipify(root: HTMLElement, whole: boolean): boolean {
  let changed = false;
  for (const node of [...root.childNodes]) {
    if (node.nodeType !== Node.TEXT_NODE) continue;
    const text = node.textContent ?? "";
    const re = /\bhttps?:\/\/[^\s<>"')\]]+/gi;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text))) {
      const end = match.index + match[0].length;
      const finished = whole || /\s/.test(text[end] ?? "");
      if (!finished) continue;
      const tail = URL_AT_END.exec(match[0]);
      const url = tail ? tail[1]! : match[0];
      const before = text.slice(0, match.index);
      const after = text.slice(match.index + url.length);
      const chip = chipElement(classify(url));
      const rest = document.createTextNode(after);
      node.parentNode!.insertBefore(document.createTextNode(before), node);
      node.parentNode!.insertBefore(chip, node);
      node.parentNode!.insertBefore(rest, node);
      node.parentNode!.removeChild(node);
      // The caret goes after the space that finished the link, where the typing was.
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.setStart(rest, Math.min(rest.length, whole ? 0 : 1));
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      changed = true;
      break;
    }
  }
  if (changed) chipify(root, whole);
  return changed;
}

/**
 * The notes, edited in place with links as chips. Saves when it loses focus.
 * Backspace takes a chip out whole; clicking one opens it.
 */
export function NotesEditor({ notes, editable, onSave }: { notes: string; editable: boolean; onSave: (notes: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const last = useRef(notes);
  const titles = useLinkTitles(linksIn(notes));

  // Drawn from the saved notes, and redrawn only when they change from outside this editor.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (document.activeElement === root && last.current === notes) return;
    last.current = notes;
    render(root, notes, titles);
  }, [notes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Names arrive after the chips are drawn: put them on.
  useEffect(() => {
    ref.current?.querySelectorAll<HTMLElement>("[data-url]").forEach((chip) => {
      const title = titles[chip.dataset.url!];
      if (title) chip.textContent = title;
    });
  }, [titles]);

  const save = () => {
    const root = ref.current;
    if (!root) return;
    const next = serialise(root).replace(/ /g, " ");
    if (next !== last.current) {
      last.current = next;
      onSave(next);
    }
  };

  return (
    <div
      ref={ref}
      className="td-notes-edit"
      contentEditable={editable}
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label="Notes"
      data-placeholder="Anything useful. Paste a Google Sheet, Doc or Drive link and it becomes a chip with the file's name."
      onInput={() => {
        if (ref.current) chipify(ref.current, false);
      }}
      onPaste={(event) => {
        event.preventDefault();
        document.execCommand("insertText", false, event.clipboardData.getData("text/plain"));
        if (ref.current) chipify(ref.current, true);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          document.execCommand("insertText", false, "\n");
        }
      }}
      onClick={(event) => {
        const chip = (event.target as HTMLElement).closest<HTMLElement>("[data-url]");
        if (chip) window.open(chip.dataset.url, "_blank", "noopener,noreferrer");
      }}
      onBlur={save}
    />
  );
}

/** Read-only notes with chips in place, for people who cannot edit. */
export function NotesView({ notes }: { notes: string }) {
  const titles = useLinkTitles(linksIn(notes));
  return (
    <div className="td-notes-view">
      {readNotes(notes).map((part, i) => ("link" in part ? <LinkChip key={i} link={part.link} title={titles[part.link.url]} /> : <span key={i}>{part.text}</span>))}
    </div>
  );
}
